import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import type { AccessAttempt, GitHost } from '../../api/admin.api';
import { ADMIN_NEW_CREDENTIAL_PATH } from '../../config';
import { cardSurfaceSx } from '../../theme';

type Props = {
  /** The host the repository is on, which decides what can be connected. */
  host: GitHost;
  message: string;
  tried: AccessAttempt[];
  /** The address being checked, handed on so the connection returns here with it filled in. */
  url: string;
};

const ROUTE: Record<AccessAttempt['kind'], (label: string) => string> = {
  public: () => 'No key',
  'github-app': (label) => `The GitHub App "${label}"`,
  'github-fine-grained-pat': (label) => `The token "${label}"`,
  'gitlab-token': (label) => `The GitLab token "${label}"`,
};

const OUTCOME: Record<AccessAttempt['outcome'], string> = {
  reached: 'got in',
  'not-installed': 'is not installed on this repository',
  refused: 'was refused',
  'cannot-see': 'cannot see it',
  unavailable: 'did not answer',
};

/**
 * **Shown when nothing the toolkit has reaches the repository.** A host answers 404
 * for a private repository it will not describe, which is the same as one that does
 * not exist, so the page cannot say which. It says what was tried, and offers what can
 * be connected to that host: for GitHub a GitHub App first, because it is not a person's
 * and does not expire, then a token; for GitLab a token, which is all it has.
 */
export const AccessOffer = ({ host, message, tried, url }: Props) => {
  const appNotInstalled = tried.some((attempt) => attempt.kind === 'github-app' && attempt.outcome === 'not-installed');
  const state = { url };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 720 }}>
      <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
        {message}
      </Typography>

      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          What was tried
        </Typography>
        <Box component="ul" sx={{ margin: 0, paddingLeft: 6 }}>
          {tried.map((attempt) => (
            <Typography component="li" variant="body2" key={attempt.credentialId ?? attempt.kind}>
              {ROUTE[attempt.kind](attempt.label)} {OUTCOME[attempt.outcome]}.
            </Typography>
          ))}
        </Box>
      </Box>

      {appNotInstalled && (
        <Typography variant="body2" role="note">
          A GitHub App is already connected. If this repository is private, ask an owner of the organisation to add it to the
          app&apos;s installation, then check again.
        </Typography>
      )}

      {host === 'gitlab.com' ? <GitLabOffer state={state} /> : <GitHubOffer state={state} />}
    </Box>
  );
};

type OfferProps = { state: { url: string } };

const GitHubOffer = ({ state }: OfferProps) => (
  <Box component="section" aria-label="Connect GitHub" sx={{ ...cardSurfaceSx, padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
    <Typography variant="h3" component="h2">
      Connect GitHub
    </Typography>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        A GitHub App (recommended)
      </Typography>
      <Typography variant="body2">
        It belongs to the organisation and not to a person who may leave, and its private key does not expire. An owner of
        the organisation makes it once and adds each repository to it.
      </Typography>
      <Box>
        <Button component={RouterLink} to={`${ADMIN_NEW_CREDENTIAL_PATH}?kind=app`} state={state} variant="contained" size="small">
          Connect a GitHub App
        </Button>
      </Box>
    </Box>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        A personal access token
      </Typography>
      <Typography variant="body2">
        It is tied to the person who makes it, and it expires. It is quicker where no organisation owner is to hand.
      </Typography>
      <Box>
        <Button component={RouterLink} to={`${ADMIN_NEW_CREDENTIAL_PATH}?kind=token`} state={state} variant="outlined" size="small">
          Add a personal access token
        </Button>
      </Box>
    </Box>
  </Box>
);

/** GitLab has no app to install, so a token is the one connection. A project access token is a bot's, not a person's. */
const GitLabOffer = ({ state }: OfferProps) => (
  <Box component="section" aria-label="Connect GitLab" sx={{ ...cardSurfaceSx, padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
    <Typography variant="h3" component="h2">
      Connect GitLab
    </Typography>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        A GitLab access token
      </Typography>
      <Typography variant="body2">
        A project access token belongs to a bot and not to a person, which is better where the plan has them. A personal
        access token is tied to whoever made it. Either expires.
      </Typography>
      <Box>
        <Button component={RouterLink} to={`${ADMIN_NEW_CREDENTIAL_PATH}?kind=gitlab`} state={state} variant="contained" size="small">
          Add a GitLab access token
        </Button>
      </Box>
    </Box>
  </Box>
);
